import { useEffect, useCallback } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import {
  setInboxUnreadCount,
  setLeadInboxUnreadCount,
  setTicketUnreadCount,
  pushInboxEvent,
} from "../../store/auth/authSlice";
import { EMAIL_API_URL } from "../../config";
import { authHeaders, getSessionToken, hasSession, COOKIE_SESSION } from "../utils/session";

// Resolves to https://stagingv2.entermark.in/api/... — same as API_BASE_URL + "/"
const BASE = `${EMAIL_API_URL}/api`;

export function useInboxBadges() {
  const dispatch = useDispatch();

  const authHdr = useCallback(
    () => ({ ...authHeaders() }),
    [],
  );

  const refetchSub = useCallback(async () => {
    try {
      const internalUserId = Number(localStorage.getItem("trade_user_id"));
      const r = await axios.get(`${BASE}/subscription-inbox/unread-count`, {
        params: { internal_user_id: internalUserId },
        headers: authHdr(),
      });
      dispatch(setInboxUnreadCount(r.data?.unread_count ?? r.data?.count ?? 0));
    } catch {}
  }, [dispatch, authHdr]);

  const refetchLead = useCallback(async () => {
    try {
      const internalUserId = Number(localStorage.getItem("trade_user_id"));
      const r = await axios.get(`${BASE}/lead-inbox/unread-count`, {
        params: { internal_user_id: internalUserId },
        headers: authHdr(),
      });
      dispatch(setLeadInboxUnreadCount(r.data?.unread_count ?? r.data?.count ?? 0));
    } catch {}
  }, [dispatch, authHdr]);

  const refetchTicket = useCallback(async () => {
    try {
      const internalUserId = Number(localStorage.getItem("trade_user_id"));
      const r = await axios.get(`${BASE}/single-email/unread-count`, {
        params: { internalUserId },
        headers: authHdr(),
      });
      dispatch(setTicketUnreadCount(r.data?.count ?? 0));
    } catch {}
  }, [dispatch, authHdr]);

  const refetchAll = useCallback(() => {
    refetchSub();
    refetchLead();
    refetchTicket();
  }, [refetchSub, refetchLead, refetchTicket]);

  useEffect(() => {
    if (!hasSession()) return;

    refetchAll(); // seed on mount

    /* EventSource cannot set headers, which is why this endpoint has always
       taken its credential in the query string. Once the session is an httpOnly
       cookie there is no string to pass — the browser carries it instead, but
       only if the stream is opened with credentials. */
    const token = getSessionToken();
    const es = COOKIE_SESSION
      ? new EventSource(`${EMAIL_API_URL}/api/events/stream`, { withCredentials: true })
      : new EventSource(
        `${EMAIL_API_URL}/api/events/stream?token=${encodeURIComponent(token)}`,
      );
    es.onmessage = (e) => {
      let ev;
      try { ev = JSON.parse(e.data); } catch { return; }
      if (ev.type !== "inbound") return;
      // Fan out to all subscribers via Redux — screens react via useSelector(selectLastInboxEvent)
      dispatch(pushInboxEvent(ev));
      // Also bump the matching badge count immediately
      if (ev.inbox === "subscription") refetchSub();
      else if (ev.inbox === "lead")    refetchLead();
      else if (ev.inbox === "ticket")  refetchTicket();
    };
    es.onerror = () => {}; // browser auto-reconnects

    const onFocus = () => refetchAll();
    window.addEventListener("focus", onFocus);

    return () => {
      es.close();
      window.removeEventListener("focus", onFocus);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
